// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { DivWrapper } from "@conklin/XDL/constants";
import color from "@conklin/XDL/Utilities/color";
import baseTheme from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import styled, { css, CSSProperties } from "styled-components";

export const layoutContainer = css`
    @media print {
        max-width: 100% !important;
        margin: 0 !important;
    }

    ${({ theme }: any) => {
        const { maxWidths }: { maxWidths: number[] } = theme.breakpoints || baseTheme.breakpoints;
        const { paddingValues = [16] }: { paddingValues: number[] } = theme.breakpoints;

        const containerStyles = maxWidths.map(
            (maxWidth, index) =>
                css`
                    &:not(.full-width) {
                        max-width: ${maxWidth}px;
                        padding-left: ${paddingValues[index] || paddingValues[0]}px;
                        padding-right: ${paddingValues[index] || paddingValues[0]}px;
                        margin-right: auto;
                        margin-left: auto;
                    }

                    &.full-width {
                        max-width: 100%;
                        margin-right: 0;
                        margin-left: 0;
                        padding-left: 0;
                        padding-right: 0;
                    }

                    &.full-width .full-width > ${DivWrapper} {
                        padding-left: 0;
                        padding-right: 0;
                    }
                `,
        );
        return css`
            ${breakpointMediaQueries(theme, containerStyles)}
        `;
    }}
`;

/**
 * Hide element in desktop and print mode
 */
export const hideElement = css`
    &&&& {
        display: none;
        @media print {
            display: none;
        }
    }
`;

/**
 * Hide element in print mode
 */
export const hideElementOnPrint = css`
    &&&& {
        @media print {
            display: none;
        }
    }
`;

/**
 * Avoid break element in print mode
 */
export const avoidBreakInsidePrintElement = css`
    @media print {
        -webkit-column-break-inside: avoid;
        page-break-inside: avoid;
        break-inside: avoid;
    }
`;

/**
 * Display flex on print
 */
export const displayFlexOnPrint = css`
    &&&& {
        @media print {
            display: flex;
        }
    }
`;

type DividerProps = {
    direction?: "vertical" | "horizontal";
    margin?: CSSProperties["margin"];
    borderColor?: string;
    printDisplay?: string;
};

export const Divider = styled.div<DividerProps>`
    background-color: ${({ borderColor }: any) =>
        css`
            ${color(borderColor || "common.border")};
        `};
    align-self: stretch;
    ${({ printDisplay }: any) => `@media print {display: ${printDisplay}}`}
    ${({ direction, margin }: any) =>
        direction === "vertical"
            ? `
                min-height: 100%;
                height: auto;
                width: 1px;
                min-width: 1px;
                margin: ${margin || `0 12px`};
                `
            : `
                width: 100%; 
                height: 1px;
                margin: ${margin || `12px 0`};
        `}
`;

type SpacerProps = {
    space?: number;
};

export const Spacer = styled.div<SpacerProps>`
    width: 100%;
    height: 0;
    ${({ space }: any) => css`
        margin: ${`${space / 2 || 8}px 0`};
    `}
`;
